import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  FileText, Loader2, Search, Calendar, Filter, X, Download,
  Users, Heart, Activity,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  ReportsCreativeCard,
  GenderCreativeCard,
  AnatomyCreativeCard,
  SmallAnnulusCreativeCard,
} from "@/components/CreativeStatCards";
import { useRbac } from "@/hooks/useRbac";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE_SIZE = 20;

const COLUMN_COUNT = 9;

const DEFAULT_COUNT_STAT = { count: 0, percentage: 0 };

const DEFAULT_REPORT_STATS = {
  totalReports: 0,
  hospitalsCount: 0,
  maleStats: { ...DEFAULT_COUNT_STAT },
  femaleStats: { ...DEFAULT_COUNT_STAT },
  anatomyStats: {
    tricuspid: { ...DEFAULT_COUNT_STAT },
    bicuspid: { ...DEFAULT_COUNT_STAT },
    viv: { ...DEFAULT_COUNT_STAT },
    tav_tav: { ...DEFAULT_COUNT_STAT },
  },
  smallAnnulusStats: { ...DEFAULT_COUNT_STAT },
};

const normalizeCountStat = (stat) => ({
  count: stat?.count ?? 0,
  percentage: stat?.percentage ?? 0,
});

const normalizeReportStats = (data = {}) => ({
  totalReports: data.total_reports ?? 0,
  hospitalsCount: data.hospitals_count ?? 0,
  maleStats: normalizeCountStat(
    data.male_stats
    ?? (data.gender_breakdown?.male != null ? { count: data.gender_breakdown.male } : DEFAULT_COUNT_STAT)
  ),
  femaleStats: normalizeCountStat(
    data.female_stats
    ?? (data.gender_breakdown?.female != null ? { count: data.gender_breakdown.female } : DEFAULT_COUNT_STAT)
  ),
  anatomyStats: {
    tricuspid: normalizeCountStat(data.anatomy_stats?.tricuspid),
    bicuspid: normalizeCountStat(data.anatomy_stats?.bicuspid),
    viv: normalizeCountStat(data.anatomy_stats?.viv),
    tav_tav: normalizeCountStat(data.anatomy_stats?.tav_tav),
  },
  smallAnnulusStats: normalizeCountStat(data.small_annulus_stats),
});

const ANATOMY_STAT_ITEMS = [
  { key: "tricuspid", title: "Tricuspid", filterValue: "Tricuspid" },
  { key: "bicuspid", title: "Bicuspid", filterValue: "Bicuspid" },
  { key: "viv", title: "ViV", filterValue: "VIV" },
  { key: "tav_tav", title: "TAV-TAV", filterValue: "TAV-TAV" },
];

const INITIAL_FILTERS = {
  patient_uhid: "",
  hospital_name: "",
  doctor_name: "",
  gender: "",
  age: "",
  report_type: "",
  extract_status: "",
  date_from: "",
  date_to: "",
  extracted_report_type: "",
  tag: "",
  anatomy_type_1: "",
  small_annulus: "",
  angulation_min: "",
  angulation_max: "",
  perimeter_derived_min: "",
  perimeter_derived_max: "",
};

const toNameList = (items) => {
  if (!Array.isArray(items)) return [];
  return items
    .map((item) => (typeof item === "string" ? item : item?.name))
    .filter(Boolean);
};

const MEASUREMENT_RANGE_FILTERS = [
  { label: "Annular angulation", minKey: "angulation_min", maxKey: "angulation_max" },
  { label: "Perimeter-derived", minKey: "perimeter_derived_min", maxKey: "perimeter_derived_max" },
];

const buildQueryParams = (filters, page) => {
  const params = { page, limit: PAGE_SIZE };

  const exactKeys = [
    "patient_uhid", "hospital_name", "doctor_name", "gender", "age", "report_type", "extract_status",
    "date_from", "date_to", "extracted_report_type", "tag", "anatomy_type_1", "small_annulus",
  ];
  exactKeys.forEach((key) => {
    if (filters[key]) params[key] = filters[key];
  });

  MEASUREMENT_RANGE_FILTERS.forEach(({ minKey, maxKey }) => {
    if (filters[minKey]) params[minKey] = filters[minKey];
    if (filters[maxKey]) params[maxKey] = filters[maxKey];
  });

  return params;
};

const buildExportParams = (filters) => ({
  ...buildQueryParams(filters, 1),
  export: true,
});

const hasActiveFilters = (filters) =>
  Object.values(filters).some((v) => v !== "" && v != null);

const formatMetric = (value, unit = "mm") => {
  if (value === null || value === undefined || value === "") return "—";
  const num = Number(value);
  if (!Number.isNaN(num)) return `${num.toFixed(1)} ${unit}`;
  return String(value);
};

const WrappedTableHead = ({ children }) => (
  <TableHead className="h-auto max-w-[7.5rem] whitespace-normal py-2.5 leading-tight align-bottom">
    {children}
  </TableHead>
);

const RangeFilterItem = ({ label, minKey, maxKey, filters, onChange }) => (
  <div className="flex flex-col gap-1 shrink-0">
    <Label className="text-[10px] text-muted-foreground whitespace-nowrap">{label}</Label>
    <div className="flex items-center gap-1">
      <Input
        type="number"
        inputMode="decimal"
        placeholder="Min"
        value={filters[minKey]}
        onChange={(e) => onChange(minKey, e.target.value)}
        className="h-8 w-[4.5rem] sm:w-20 text-xs"
        data-testid={`filter-${minKey}`}
        aria-label={`${label} minimum`}
      />
      <span className="text-xs text-muted-foreground">–</span>
      <Input
        type="number"
        inputMode="decimal"
        placeholder="Max"
        value={filters[maxKey]}
        onChange={(e) => onChange(maxKey, e.target.value)}
        className="h-8 w-[4.5rem] sm:w-20 text-xs"
        data-testid={`filter-${maxKey}`}
        aria-label={`${label} maximum`}
      />
    </div>
  </div>
);

export default function Reports() {
  const navigate = useNavigate();
  const location = useLocation();
  const { canExportReportsCsv } = useRbac();
  const [reports, setReports] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [debouncedFilters, setDebouncedFilters] = useState(INITIAL_FILTERS);
  const [exporting, setExporting] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [stats, setStats] = useState(DEFAULT_REPORT_STATS);
  const [statsLoading, setStatsLoading] = useState(true);
  const [hospitals, setHospitals] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [tags, setTags] = useState([]);
  const [filterOptionsLoading, setFilterOptionsLoading] = useState(true);
  const statsRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedFilters(filters), 400);
    return () => clearTimeout(timer);
  }, [filters]);

  const clearStatFilters = useCallback(() => {
    setFilters((prev) => {
      if (!prev.gender && !prev.anatomy_type_1 && !prev.small_annulus) return prev;
      return { ...prev, gender: "", anatomy_type_1: "", small_annulus: "" };
    });
    setPage(1);
  }, []);

  useEffect(() => {
    const hasStatFilter = Boolean(
      filters.gender || filters.anatomy_type_1 || filters.small_annulus,
    );
    if (!hasStatFilter) return undefined;

    const handlePointerDown = (e) => {
      if (statsRef.current?.contains(e.target)) return;
      clearStatFilters();
    };

    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, [filters.gender, filters.anatomy_type_1, filters.small_annulus, clearStatFilters]);

  useEffect(() => {
    const loadStats = async () => {
      setStatsLoading(true);
      try {
        const res = await axios.get(`${API}/reports/stats`);
        setStats(normalizeReportStats(res.data));
      } catch (e) {
        console.error("Failed to load report stats", e);
      } finally {
        setStatsLoading(false);
      }
    };
    loadStats();
  }, []);

  useEffect(() => {
    const loadFilterOptions = async () => {
      setFilterOptionsLoading(true);
      try {
        const [hospitalsRes, doctorsRes, tagsRes] = await Promise.all([
          axios.get(`${API}/reports/hospitals`),
          axios.get(`${API}/reports/doctors`),
          axios.get(`${API}/patients/ref/tags`),
        ]);
        setHospitals(toNameList(hospitalsRes.data?.hospitals ?? hospitalsRes.data));
        setDoctors(toNameList(doctorsRes.data?.doctors ?? doctorsRes.data));
        const tagList = Array.isArray(tagsRes.data?.tags)
          ? tagsRes.data.tags
          : Array.isArray(tagsRes.data)
            ? tagsRes.data
            : [];
        setTags(tagList.filter(Boolean).map(String));
      } catch (e) {
        console.error("Failed to load report filter options", e);
        setHospitals([]);
        setDoctors([]);
        setTags([]);
      } finally {
        setFilterOptionsLoading(false);
      }
    };
    loadFilterOptions();
  }, []);

  const updateFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const toggleStatFilter = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: prev[key] === value ? "" : value,
    }));
    setPage(1);
  };

  const clearFilters = () => {
    setFilters(INITIAL_FILTERS);
    setPage(1);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = buildQueryParams(debouncedFilters, page);
      const r = await axios.get(`${API}/reports`, { params });
      setReports(r.data.reports || []);
      setTotal(r.data.total ?? 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load reports");
    } finally {
      setLoading(false);
    }
  }, [debouncedFilters, page]);

  useEffect(() => { load(); }, [load]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = buildExportParams(debouncedFilters);
      const res = await axios.get(`${API}/reports`, {
        params,
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "reports_export.csv";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Export downloaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to export reports");
    } finally {
      setExporting(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const filtersActive = useMemo(() => hasActiveFilters(filters), [filters]);

  return (
    <div className="space-y-4" data-testid="reports-page">
      <div
        ref={statsRef}
        className="grid grid-cols-1 sm:grid-cols-2 items-stretch gap-3 lg:grid-cols-[1.25fr_0.8fr_1.15fr_1.3fr] [&>*]:min-h-[13rem]"
        data-testid="reports-stats"
      >
        <ReportsCreativeCard
          total={stats.totalReports}
          hospitals={stats.hospitalsCount}
          icon={FileText}
          glowClass="stat-glow-sky"
          delay="animate-fade-in-delay-1"
          loading={statsLoading}
        />
        <GenderCreativeCard
          femaleCount={stats.femaleStats.count}
          femalePercentage={stats.femaleStats.percentage}
          maleCount={stats.maleStats.count}
          malePercentage={stats.maleStats.percentage}
          femaleActive={filters.gender === "female"}
          maleActive={filters.gender === "male"}
          onFemaleClick={() => toggleStatFilter("gender", "female")}
          onMaleClick={() => toggleStatFilter("gender", "male")}
          icon={Users}
          glowClass="stat-glow-amber"
          delay="animate-fade-in-delay-2"
          loading={statsLoading}
          className="sm:col-span-2 lg:col-span-1"
        />
        <AnatomyCreativeCard
          icon={Heart}
          glowClass="stat-glow-indigo"
          delay="animate-fade-in-delay-3"
          loading={statsLoading}
          className="sm:col-span-2 lg:col-span-1"
          items={ANATOMY_STAT_ITEMS.map(({ key, title, filterValue }) => {
            const anatomyStat = stats.anatomyStats?.[key];
            return {
              key,
              title,
              value: anatomyStat?.count ?? 0,
              percentage: anatomyStat?.percentage,
              active: filters.anatomy_type_1 === filterValue,
              onClick: () => toggleStatFilter("anatomy_type_1", filterValue),
            };
          })}
        />
        <SmallAnnulusCreativeCard
          value={stats.smallAnnulusStats.count}
          percentage={stats.smallAnnulusStats.percentage}
          icon={Activity}
          glowClass="stat-glow-green"
          delay="animate-fade-in-delay-4"
          loading={statsLoading}
          active={filters.small_annulus === "true"}
          onClick={() => toggleStatFilter("small_annulus", "true")}
        />
      </div>

      <Collapsible open={filtersOpen} onOpenChange={setFiltersOpen}>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <p className="text-sm text-muted-foreground">
            {loading ? "Loading…" : `${total} report${total !== 1 ? "s" : ""}`}
            {filtersActive ? " (filtered)" : ""}
          </p>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            <CollapsibleTrigger asChild>
              <Button
                type="button"
                variant="outline"
                size="sm"
                data-testid="reports-filters-toggle"
                aria-expanded={filtersOpen}
                className="gap-1.5"
              >
                <Filter className="w-3.5 h-3.5" />
                Filters
                {filtersActive ? (
                  <Badge variant="secondary" className="text-[10px] px-1.5 py-0 font-normal">
                    Active
                  </Badge>
                ) : null}
              </Button>
            </CollapsibleTrigger>
            {canExportReportsCsv ? (
              <Button
                variant="outline"
                size="sm"
                onClick={handleExport}
                disabled={exporting || loading}
                data-testid="reports-export-btn"
                className="gap-1.5"
              >
                {exporting ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Download className="w-3.5 h-3.5" />
                )}
                {exporting ? "Exporting…" : "Export CSV"}
              </Button>
            ) : null}
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1 || loading}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              data-testid="reports-prev-page"
            >
              Previous
            </Button>
            <span className="text-xs text-muted-foreground">
              Page {page} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages || loading}
              onClick={() => setPage((p) => p + 1)}
              data-testid="reports-next-page"
            >
              Next
            </Button>
          </div>
        </div>

        <CollapsibleContent className="data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down overflow-hidden">
          <Card className="p-4 mt-3">
            {filtersActive ? (
              <div className="flex items-center justify-end gap-2 mb-3">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearFilters}
                  data-testid="clear-report-filters"
                  className="text-xs gap-1 shrink-0 h-8"
                >
                  <X className="w-3.5 h-3.5" /> Clear all
                </Button>
              </div>
            ) : null}
            <div className="flex flex-wrap items-end gap-2 sm:gap-3" data-testid="measurement-range-filters">
            <div className="relative w-full sm:w-40 shrink-0">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
              <Input
                data-testid="filter-patient-uhid"
                placeholder="Search by UHID..."
                value={filters.patient_uhid}
                onChange={(e) => updateFilter("patient_uhid", e.target.value)}
                className="pl-8 h-9 text-xs"
              />
            </div>
            <Select
              value={filters.hospital_name || "all"}
              onValueChange={(v) => updateFilter("hospital_name", v === "all" ? "" : v)}
              disabled={filterOptionsLoading}
            >
              <SelectTrigger data-testid="filter-hospital-name" className="w-full sm:w-44 h-9">
                <SelectValue placeholder={filterOptionsLoading ? "Loading hospitals…" : "Hospital"} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All hospitals</SelectItem>
                {hospitals.map((name) => (
                  <SelectItem key={name} value={name}>
                    {name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={filters.doctor_name || "all"}
              onValueChange={(v) => updateFilter("doctor_name", v === "all" ? "" : v)}
              disabled={filterOptionsLoading}
            >
              <SelectTrigger data-testid="filter-doctor-name" className="w-full sm:w-40 h-9">
                <SelectValue placeholder={filterOptionsLoading ? "Loading doctors…" : "Doctor"} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All doctors</SelectItem>
                {doctors.map((name) => (
                  <SelectItem key={name} value={name}>
                    {name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={filters.gender || "all"}
              onValueChange={(v) => updateFilter("gender", v === "all" ? "" : v)}
            >
              <SelectTrigger data-testid="filter-gender" className="w-full sm:w-32 h-9">
                <SelectValue placeholder="Gender" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All genders</SelectItem>
                <SelectItem value="female">Female</SelectItem>
                <SelectItem value="male">Male</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
            <Input
              data-testid="filter-age"
              type="number"
              inputMode="numeric"
              min={0}
              placeholder="Age"
              value={filters.age}
              onChange={(e) => updateFilter("age", e.target.value)}
              className="h-9 w-full sm:w-20 text-xs"
              aria-label="Patient age"
            />
            <Select
              value={filters.extract_status || "all"}
              onValueChange={(v) => updateFilter("extract_status", v === "all" ? "" : v)}
            >
              <SelectTrigger data-testid="filter-extract-status" className="w-full sm:w-36 h-9">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
                <SelectItem value="skipped">Skipped</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={filters.report_type || "all"}
              onValueChange={(v) => updateFilter("report_type", v === "all" ? "" : v)}
            >
              <SelectTrigger data-testid="filter-report-type" className="w-full sm:w-40 h-9">
                <SelectValue placeholder="Report type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All report types</SelectItem>
                <SelectItem value="signed_report">Signed report</SelectItem>
                <SelectItem value="clinical_report">Clinical report</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={filters.extracted_report_type || "all"}
              onValueChange={(v) => updateFilter("extracted_report_type", v === "all" ? "" : v)}
            >
              <SelectTrigger data-testid="filter-extracted-report-type" className="w-full sm:w-44 h-9">
                <SelectValue placeholder="Extracted type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All extracted types</SelectItem>
                <SelectItem value="tavr_planning_3mensio">TAVR · 3mensio</SelectItem>
                <SelectItem value="generic_cardiac_ct">Cardiac CT</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={filters.tag || "all"}
              onValueChange={(v) => updateFilter("tag", v === "all" ? "" : v)}
              disabled={filterOptionsLoading}
            >
              <SelectTrigger data-testid="filter-tag" className="w-full sm:w-36 h-9">
                <SelectValue placeholder={filterOptionsLoading ? "Loading tags…" : "Tag"} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Tags</SelectItem>
                {tags.map((t) => (
                  <SelectItem key={t} value={t}>{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <Calendar className="w-3.5 h-3.5 flex-shrink-0 text-muted-foreground" />
              <Input
                data-testid="filter-date-from"
                type="date"
                value={filters.date_from}
                onChange={(e) => updateFilter("date_from", e.target.value)}
                className="h-9 w-full sm:w-36 text-xs"
                aria-label="Imported from date"
              />
              <span className="text-xs text-muted-foreground shrink-0">to</span>
              <Input
                data-testid="filter-date-to"
                type="date"
                value={filters.date_to}
                onChange={(e) => updateFilter("date_to", e.target.value)}
                className="h-9 w-full sm:w-36 text-xs"
                aria-label="Imported to date"
              />
            </div>
            {MEASUREMENT_RANGE_FILTERS.map((cfg) => (
              <RangeFilterItem
                key={cfg.minKey}
                {...cfg}
                filters={filters}
                onChange={updateFilter}
              />
            ))}
            </div>
          </Card>
        </CollapsibleContent>
      </Collapsible>

      <Card className="overflow-hidden">
        <div className="overflow-auto h-[calc(100vh-320px)] sm:h-[calc(100vh-280px)]">
          <Table className="sticky-table w-full">
            <TableHeader>
              <TableRow>
                <TableHead>Patient Name</TableHead>
                <TableHead>Doctor Name</TableHead>
                <TableHead>Age</TableHead>
                <WrappedTableHead>
                  Perimeter
                  <br />
                  derived
                </WrappedTableHead>
                <WrappedTableHead>
                  Annulus Perimeter
                  <br />
                  (mm)
                </WrappedTableHead>
                <WrappedTableHead>
                  LVOT Perimeter
                  <br />
                  (mm)
                </WrappedTableHead>
                <WrappedTableHead>
                  Annular Angulation
                  <br />
                  (deg)
                </WrappedTableHead>
                <WrappedTableHead>
                  Coronary Left
                  <br />
                  Height (mm)
                </WrappedTableHead>
                <WrappedTableHead>
                  Coronary Right
                  <br />
                  Height (mm)
                </WrappedTableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={COLUMN_COUNT} className="text-center py-8 text-muted-foreground">
                    <Loader2 className="w-4 h-4 mx-auto mb-2 animate-spin" />
                    Loading…
                  </TableCell>
                </TableRow>
              ) : reports.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={COLUMN_COUNT} className="text-center py-12">
                    <FileText className="w-8 h-8 mx-auto mb-3 text-muted-foreground" />
                    <div className="text-sm font-medium">
                      {filtersActive ? "No reports match these filters" : "No reports yet"}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {filtersActive
                        ? "Try adjusting or clearing filters."
                        : <>Open a study and use <span className="font-medium">Import Report</span> to bring a PDF in.</>}
                    </div>
                  </TableCell>
                </TableRow>
              ) : reports.map((r, i) => {
                const ex = r.extracted || {};
                const identity = ex?.patient_identity || {};
                const name = identity?.name || r.patient_uhid || "—";

                return (
                  <TableRow
                    key={r.id}
                    data-testid={`report-row-${i}`}
                    className="cursor-pointer hover:bg-secondary/50"
                    onClick={() => navigate(`/reports/${r.id}`, {
                      state: { from: `${location.pathname}${location.search}` },
                    })}
                  >
                    <TableCell className="font-medium">
                      {name}
                    </TableCell>
                    <TableCell className="text-xs">
                      {identity?.physician_name || "—"}
                    </TableCell>
                    <TableCell className="text-xs font-medium">
                      {identity?.age != null && identity?.age !== "" ? identity.age : "—"}
                    </TableCell>
                    <TableCell className="text-xs font-medium">
                      {formatMetric(ex?.annulus?.perimeter_derived_diameter_mm)}
                    </TableCell>
                    <TableCell className="text-xs font-medium">
                      {formatMetric(ex?.annulus?.perimeter_mm)}
                    </TableCell>
                    <TableCell className="text-xs font-medium">
                      {formatMetric(ex?.lvot?.perimeter_mm)}
                    </TableCell>
                    <TableCell className="text-xs font-medium">
                      {formatMetric(ex?.annular_angulation_degrees, "°")}
                    </TableCell>
                    <TableCell className="text-xs font-medium">
                      {formatMetric(ex?.coronary_ostia?.left_height_mm)}
                    </TableCell>
                    <TableCell className="text-xs font-medium">
                      {formatMetric(ex?.coronary_ostia?.right_height_mm)}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}
